import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Target, ArrowsClockwise, Star, Info, ChartLineUp, Trophy, Sparkle } from "@phosphor-icons/react";

/**
 * Job-coach view of a coachee's Competentietest.
 * - Read-only view of the 20 competencies + self-scores + 360 averages.
 * - "Genereer opnieuw"-knop met popup voor coach-guidance prompt — niet
 *   geblokkeerd door de coachee-limiet en wel toegestaan wanneer bronnen
 *   onveranderd zijn.
 */
export default function CompetenceCoachPanel({ coacheeId, coacheeName }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [guidance, setGuidance] = useState("");
  const [error, setError] = useState("");

  const fetchSession = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/coachees/${coacheeId}/competence`);
      setSession(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSession(); }, [coacheeId]);

  const handleRegenerate = async () => {
    setGenerating(true);
    setError("");
    try {
      const { data } = await api.post(
        `/coachees/${coacheeId}/competence/coach-regenerate`,
        { coach_guidance: guidance.trim() },
        { timeout: 180000 },
      );
      setSession(data);
      setConfirmOpen(false);
      setGuidance("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setGenerating(false);
    }
  };

  const competencies = session?.competencies || [];
  const hasCompetencies = competencies.length > 0;
  const feedbackAvg = session?.feedback_avg || {};
  const selfScores = session?.self_scores || {};
  const underu = session?.underutilised_top3 || [];

  return (
    <div className="space-y-4 max-w-3xl" data-testid="competence-coach-panel">
      <div className="neuro-card-bold p-5 bg-white">
        <div className="flex items-start gap-3 flex-wrap">
          <Target size={26} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
          <div className="flex-1 min-w-[240px]">
            <h3 className="font-display text-lg font-bold tracking-tight">Competentietest</h3>
            <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
              {coacheeName || "De coachee"} kan deze test {session?.coachee_regen_limit ?? 1}× zelf
              opnieuw laten genereren. Daarna kun jij vanuit hier opnieuw genereren — eventueel met
              specifieke wensen voor welke competenties je wilt zien.
            </p>
            {session && (
              <div className="text-xs text-zinc-500 mt-2" data-testid="competence-coach-meta">
                {hasCompetencies
                  ? `Versie ${session.version} · ${session.feedback_count} 360°-feedback inzendingen · `
                  : "Nog niet gegenereerd · "}
                Coachee gebruikt: {session.coachee_regen_count}/{session.coachee_regen_limit} regeneraties
                {(session.previous_versions_count || 0) > 0 &&
                  ` · ${session.previous_versions_count} gearchiveerde versie(s)`}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={generating}
            data-testid="competence-coach-regen-btn"
            className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] disabled:bg-zinc-400 transition-colors"
          >
            <ArrowsClockwise size={16} /> Genereer opnieuw
          </button>
        </div>
      </div>

      {error && (
        <div className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm" data-testid="competence-coach-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-zinc-500 text-sm">Laden...</div>
      ) : !hasCompetencies ? (
        <div className="neuro-card p-6 bg-white" data-testid="competence-coach-empty">
          <div className="flex items-start gap-3">
            <Info size={22} className="text-[#1E88E5] shrink-0 mt-0.5" />
            <div>
              <h4 className="font-display text-base font-bold tracking-tight">Nog niet gegenereerd</h4>
              <p className="text-sm text-zinc-600 mt-1">
                {coacheeName || "De coachee"} heeft de competentielijst nog niet laten genereren. Klik
                op "Genereer opnieuw" hierboven om dat voor de coachee te doen.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="neuro-card p-4 bg-white space-y-2" data-testid="competence-coach-list">
            {competencies.map((c, i) => {
              const own = selfScores[c.name];
              const fb = feedbackAvg[c.name] || { avg: null, count: 0 };
              return (
                <div
                  key={c.name}
                  className="grid grid-cols-1 md:grid-cols-12 gap-3 py-2.5 border-b border-zinc-100 last:border-b-0 items-center"
                  data-testid={`competence-coach-row-${i}`}
                >
                  <div className="md:col-span-6 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {c.talent_match && (
                        <span className="inline-flex items-center justify-center h-5 w-5 bg-[#FB8C00] text-white rounded-sm text-[11px] font-bold">✦</span>
                      )}
                      <span className="font-display text-sm font-bold tracking-tight">{c.name}</span>
                      {c.preserved === false && (
                        <span className="inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-widest bg-[#1E88E5]/10 text-[#1E88E5] border border-[#1E88E5]/30 rounded-sm">
                          nieuw
                        </span>
                      )}
                    </div>
                    {c.description && <p className="text-xs text-zinc-600 mt-0.5">{c.description}</p>}
                  </div>
                  <div className="md:col-span-3 text-xs">
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Eigen</span>
                    {own ? (
                      <span className="inline-flex items-center gap-0.5"><Star size={12} weight="fill" className="text-[#FB8C00]" />{own}/5</span>
                    ) : (
                      <span className="text-zinc-400">—</span>
                    )}
                  </div>
                  <div className="md:col-span-3 text-xs">
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">360°</span>
                    {fb.avg != null ? (
                      <span className="inline-flex items-center gap-0.5">
                        <Star size={12} weight="fill" className="text-[#FB8C00]" />
                        {fb.avg.toFixed(1)}/5 <span className="text-zinc-400 ml-0.5">({fb.count})</span>
                      </span>
                    ) : (
                      <span className="text-zinc-400">—</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {underu.length > 0 && (
            <div className="neuro-card-bold p-5 bg-white" data-testid="competence-coach-underu">
              <div className="flex items-start gap-3">
                <Trophy size={22} weight="duotone" className="text-[#FB8C00] shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-display text-base font-bold tracking-tight">Top 3 onderbenutte talenten</h4>
                  <ol className="mt-2 space-y-1.5 text-sm">
                    {underu.map((it, i) => (
                      <li key={it.name} className="flex gap-2">
                        <span className="font-bold text-zinc-500">{i + 1}.</span>
                        <span>
                          <strong>{it.name}</strong>
                          <span className="text-xs text-zinc-500 ml-2">
                            (eigen {it.self_score ?? "—"}/5, 360° {it.feedback_avg != null ? it.feedback_avg.toFixed(1) : "—"}/5)
                          </span>
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !generating && setConfirmOpen(false)}>
          <form
            onSubmit={(e) => { e.preventDefault(); handleRegenerate(); }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white p-6 max-w-lg w-full rounded-sm shadow-xl space-y-4"
            data-testid="competence-coach-regen-modal"
          >
            <div className="flex items-start gap-3">
              <Sparkle size={22} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
              <div>
                <h4 className="font-display text-lg font-bold tracking-tight">Competenties opnieuw genereren</h4>
                <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
                  De AI behoudt zoveel mogelijk de bestaande 20 competenties en vervangt alleen
                  competenties waar een goede reden voor is. Bestaande scores op behouden
                  competenties blijven staan; nieuwe competenties beginnen leeg.
                </p>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1.5">
                Specifieke wensen voor de AI (optioneel)
              </label>
              <textarea
                value={guidance}
                onChange={(e) => setGuidance(e.target.value)}
                rows={4}
                placeholder="Bijv. 'Voeg meer commerciële competenties toe' of 'Focus op leidinggevende vaardigheden'. De AI geeft jouw wensen voorrang."
                data-testid="competence-coach-guidance"
                className="w-full px-3 py-2 border-2 border-zinc-200 rounded-sm text-sm focus:outline-none focus:border-zinc-900 resize-y"
              />
              <p className="text-[11px] text-zinc-500 mt-1">{guidance.length}/1500 tekens</p>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={generating}
                data-testid="competence-coach-regen-cancel"
                className="px-4 py-2 text-sm font-medium border border-zinc-300 rounded-sm hover:bg-zinc-100 transition-colors"
              >
                Annuleren
              </button>
              <button
                type="submit"
                disabled={generating}
                data-testid="competence-coach-regen-confirm"
                className="px-5 py-2 text-sm font-semibold tracking-wide uppercase bg-zinc-900 text-white rounded-sm hover:bg-[#1E88E5] disabled:bg-zinc-400 transition-colors"
              >
                {generating ? "Genereren..." : "Genereer nu"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
